package com.example.borshi.web.nutritionist.models

/**
 * Base plan template created by Nutritionist with rules and constraints
 * Part of the Nutritionist Module - can be extracted as independent module
 */
data class NutritionistBasePlan(
    val basePlanId: String = "",
    val nutritionistId: String = "",
    val nutritionistName: String = "",

    // Plan Info
    val planName: String = "",
    val category: PlanCategory = PlanCategory.GENERAL_HEALTH,
    val targetAudience: String = "",
    val description: String = "",
    val goals: List<String> = emptyList(),

    // Nutritional Requirements (Weekly targets)
    val nutritionalTargets: NutritionalTargets = NutritionalTargets(),

    // Rules & Constraints
    val mandatoryInclusions: List<MandatoryInclusion> = emptyList(),
    val exclusions: List<PlanExclusion> = emptyList(),
    val recommendedProducts: List<RecommendedProduct> = emptyList(),
    val customizationRules: CustomizationRules = CustomizationRules(),

    // Delivery & Pricing
    val deliveryScheduleTemplate: DeliveryScheduleTemplate = DeliveryScheduleTemplate(),
    val pricingTemplate: PricingTemplate = PricingTemplate(),

    // UI/Display
    val imageUrl: String = "",
    val badgeText: String = "",
    val badgeColor: String = "",
    val displayOrder: Int = 0,
    val benefits: List<PlanBenefit> = emptyList(),

    // Monitoring
    val requiresProgressTracking: Boolean = true,
    val reviewIntervalWeeks: Int = 4,

    // Status & Metadata
    val isActive: Boolean = true,
    val isPublic: Boolean = true, // Can users self-select?
    val createdAt: Long = 0,
    val updatedAt: Long = 0,
    val version: Int = 1
)

/**
 * Plan categories for different health goals
 */
enum class PlanCategory(val displayName: String) {
    MUSCLE_BUILDING("Muscle Building"),
    WEIGHT_LOSS("Weight Loss"),
    GENERAL_HEALTH("General Health"),
    PREGNANCY("Pregnancy"),
    SENIOR("Senior Health"),
    ATHLETE("Athlete Performance"),
    DIABETES("Diabetes Management"),
    HEART_HEALTH("Heart Health")
}

/**
 * Nutritional targets for the plan
 */
data class NutritionalTargets(
    val proteinPerWeek: Int = 0, // grams
    val omega3PerWeek: Int = 0, // mg
    val caloriesPerDay: Int = 0,
    val fatPerDay: Int = 0, // grams
    val carbsPerDay: Int = 0 // grams
)

/**
 * Mandatory inclusion rules (must have in plan)
 */
data class MandatoryInclusion(
    val category: String = "", // e.g., "high_protein_fish"
    val minQuantityPerWeek: Int = 0, // grams
    val reason: String = "",
    val examples: List<String> = emptyList(),
    val isFlexible: Boolean = false // Can user choose which items?
)

/**
 * Exclusion rules (not allowed in plan)
 */
data class PlanExclusion(
    val type: ExclusionType = ExclusionType.INGREDIENT,
    val values: List<String> = emptyList(),
    val reason: String = "",
    val isOptional: Boolean = false // Can user override?
)

enum class ExclusionType {
    INGREDIENT,
    COOKING_METHOD,
    TAG,
    PRODUCT_CATEGORY
}

/**
 * Recommended product by nutritionist
 */
data class RecommendedProduct(
    val productId: String = "",
    val productName: String = "",
    val quantityPerWeek: Int = 0,
    val frequency: String = "weekly",
    val priority: ProductPriority = ProductPriority.MEDIUM,
    val alternativesAllowed: Boolean = true,
    val alternativeCategories: List<String> = emptyList(),
    val nutritionBenefit: String = ""
)

enum class ProductPriority {
    HIGH,    // Must have
    MEDIUM,  // Recommended
    LOW      // Optional
}

/**
 * Customization rules defining what users can change
 */
data class CustomizationRules(
    val canSwapProducts: Boolean = true,
    val canChangeQuantity: Boolean = true,
    val quantityVariancePercent: Int = 20, // ±20%
    val mustMeetProteinTarget: Boolean = true,
    val mustMeetOmega3Target: Boolean = true,
    val canAddExtraItems: Boolean = true,
    val maxExtraItems: Int = 2,
    val requiresApproval: Boolean = false,
    val allowedFoodPreferences: List<String> = emptyList()
)

/**
 * Delivery schedule template
 */
data class DeliveryScheduleTemplate(
    val recommendedFrequency: String = "weekly",
    val recommendedDeliveryDay: String = "saturday",
    val flexibleSchedule: Boolean = true,
    val minDeliveriesPerWeek: Int = 1,
    val maxDeliveriesPerWeek: Int = 3
)

/**
 * Pricing template
 */
data class PricingTemplate(
    val weeklyBase: Double = 0.0,
    val allowDiscounts: Boolean = true,
    val customPricingRules: List<String> = emptyList()
)

/**
 * Benefits of the plan
 */
data class PlanBenefit(
    val benefitId: String = "",
    val icon: String = "",
    val title: String = "",
    val description: String = "",
    val isHighlighted: Boolean = false // Important benefits to emphasize?
)

/**
 * Plan item customization tracking
 * Used when assigning a plan to track modifications
 */
data class PlanItemCustomization(
    val productId: String = "",
    val productName: String = "",
    val originalQuantity: Int = 0,
    val customQuantity: Int = 0,
    val isRemoved: Boolean = false,
    val substitutionProductId: String? = null,
    val customNote: String = ""
)
